import type {Ref} from 'vue'
import {insuranceRuntimeStore, runtimeStore} from '@/composables/store-accessor'
import {appInstance} from '@/utils/app-accessor'
import {UTCToDate} from '@/utils/helpers'
import {
    filters as filtersBlank,
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
} from '~/utils/insurance/insurance-blank-states'
import {isAfter, isBefore, parseISO} from 'date-fns'
import {salesTermsRsTransformer} from '@/utils/api-helpers'
import {useProductStore} from '~/composables/product-store'

export const useInsuranceStore = defineStore('insurance', () => {
    const searchExpirationTime: Ref = ref(null)
    const searchRequest: Ref = ref(searchRequestBlank())
    const searchResponse: Ref = ref(searchResponseBlank())
    const sortFnName: Ref = ref('priceAsc')
    const filters: Ref = ref(filtersBlank())
    const prepareBookRequest: Ref = ref({})
    const prepareBookResponse: Ref = ref({})
    const basket: Ref = ref([])

    function _getBasketItem(offerKey: any) {
        return (
            basket.value.find((item: any) => item.offerKey === offerKey) || {
                searchRequest: searchRequestBlank(),
                product: {offers: [], info: {}},
                info: {},
            }
        )
    }

    function NEW_SEARCH(_searchRequest: any) {
        searchExpirationTime.value = null
        searchResponse.value = searchResponseBlank()
        searchRequest.value = _searchRequest
    }

    function RESET() {
        searchExpirationTime.value = null
        searchResponse.value = searchResponseBlank()
        searchRequest.value = searchRequestBlank()

        prepareBookRequest.value = {}
        prepareBookResponse.value = {}
    }

    function SET_SEARCH_REQUEST(rq: any) {
        searchRequest.value = rq
    }

    function SET_SEARCH_RESPONSE(rs: any) {
        searchResponse.value = rs
    }

    function SET_SEARCH_EXPIRATION_TIME(date: any) {
        searchExpirationTime.value = date
    }

    function SET_PREPARE_BOOK_RESPONSE(_prepareBookResponse: any) {
        prepareBookResponse.value = _prepareBookResponse
    }

    function SET_PREPARE_BOOK_REQUEST(_prepareBookRequest: any) {
        prepareBookRequest.value = _prepareBookRequest
    }

    function SET_PREPARE_BOOK_REQUEST_PROP(data: any) {
        prepareBookRequest.value[data.prop] = data.val
    }

    function RESET_FILTERS() {
        filters.value = filtersBlank()
    }

    function SET_FILTER({key, value}: any) {
        filters.value[key] = value
    }

    function SET_FILTERS(val: any) {
        filters.value = val
    }

    function SET_SORT_FN_NAME(sort: any) {
        sortFnName.value = sort
    }

    function ADD_TO_BASKET(offerKey: any) {
        basket.value = basket.value.filter((item: any) => {
            const offer = item.product.offers.find((offer: any) => offer.offerKey === item.offerKey)
            return isBefore(new Date(), UTCToDate(parseISO(offer.expirationTime)))
        })
        if (_getBasketItem(offerKey).offerKey) return
        const product = searchResponse.value.products.find(
            (product: any) => product.offers.findIndex((offer: any) => offer.offerKey === offerKey) !== -1
        )
        basket.value.push({
            offerKey,
            product,
            searchRequest: {...searchRequest.value},
        })
    }

    function REFRESH_BASKET_PRICE({offerKey, prepareBookResponse}: any) {
        const basketItem = _getBasketItem(offerKey)
        if (!basketItem.offerKey) return
        const offer = basketItem.product.offers.find((offer: any) => offer.offerKey === offerKey)
        const {price} = salesTermsRsTransformer(prepareBookResponse.currentSalesTerms)

        offer.currentPrice = price
    }

    async function prepareBook(req: any) {
        runtimeStore.SET_BOOKING_ACTIVE(true)
        try {
            SET_PREPARE_BOOK_REQUEST(req)
            const response = await appInstance.$api.prepareInsuranceBook.post(req)
            SET_PREPARE_BOOK_RESPONSE(response)
            return response
        } finally {
            runtimeStore.SET_BOOKING_ACTIVE(false)
        }
    }

    const hasOffers = computed(() => {
        return !!searchResponse.value.products.length
    })

    const basketItem = computed(() => {
        return (offerKey: any) => {
            return _getBasketItem.call(this, offerKey)
        }
    })

    const isOffersExpired = computed(() => {
        return () =>
            hasOffers.value && !insuranceRuntimeStore.searchActive && isAfter(new Date(), searchExpirationTime.value)
    })

    return {
        ...useProductStore(searchResponse),
        searchExpirationTime,
        searchRequest,
        searchResponse,
        sortFnName,
        filters,
        prepareBookRequest,
        prepareBookResponse,
        basket,
        NEW_SEARCH,
        RESET,
        SET_SEARCH_REQUEST,
        SET_SEARCH_RESPONSE,
        SET_SEARCH_EXPIRATION_TIME,
        SET_PREPARE_BOOK_RESPONSE,
        SET_PREPARE_BOOK_REQUEST,
        SET_PREPARE_BOOK_REQUEST_PROP,
        RESET_FILTERS,
        SET_FILTER,
        SET_FILTERS,
        SET_SORT_FN_NAME,
        ADD_TO_BASKET,
        REFRESH_BASKET_PRICE,
        prepareBook,
        hasOffers,
        basketItem,
        isOffersExpired,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useInsuranceStore, import.meta.hot))
